import React from "react";
import type { MRT_ColumnDef } from "material-react-table";
import { formatGBP, isFormattableAmount, parseGBP } from "../../utils/currency";
import { isMonetaryField } from "../../shared/monetaryFields";

/**
 * Renders a monetary cell value. Non-numeric content (a dash, "N/A", a status
 * string) is passed through untouched so generic grids do not mangle it.
 */
export const CurrencyCell: React.FC<{
  value: unknown;
  blank?: string;
  currency?: string;
}> = ({ value, blank, currency }) =>
  isFormattableAmount(value) ? (
    <>{formatGBP(value as number | string, { blank, currency })}</>
  ) : (
    <>{(value as React.ReactNode) ?? blank ?? ""}</>
  );

export type CurrencyColumnOptions = {
  /** Rendered when the value is absent. Defaults to "". */
  blank?: string;
  /** Row property holding an ISO currency code, when the row is not GBP. */
  currencyField?: string;
};

/**
 * Sorts on the amount, whatever shape it arrives in.
 *
 * The API returns SQL DECIMAL columns as JSON strings ("1000.00"), and a plain
 * `a > b` comparison sorts those alphabetically — "1000.00" before "556.00".
 * `parseGBP` also copes with the grouped/prefixed text that monetary `type:
 * "text"` fields hold. Rows with no amount at all sort together at the bottom
 * ascending rather than scattering through the numbers.
 */
const amountSortingFn = <T,>(
  rowA: any,
  rowB: any,
  columnId: string,
): number => {
  const a = parseGBP(rowA.getValue(columnId));
  const b = parseGBP(rowB.getValue(columnId));

  if (a === null && b === null) return 0;
  if (a === null) return -1;
  if (b === null) return 1;
  return a === b ? 0 : a > b ? 1 : -1;
};

/**
 * Builds a material-react-table column that DISPLAYS UK Sterling while leaving the
 * underlying accessor value untouched.
 *
 * Only `Cell` (the renderer) is overridden — never `accessorFn` — so column
 * filtering, aggregation and row totals keep operating on the raw value.
 * Formatting the accessor instead would sort "£1,000,000.00" below "£556.00".
 *
 * `filterVariant` is deliberately NOT set: forcing a range filter would swap the
 * text box users have today for a min/max pair on every monetary column, and any
 * string-based `filterFn` a table supplies from config would then be handed
 * `[min, max]` instead of a string. Tables opt into range filtering themselves.
 */
export const currencyColumn = <T extends Record<string, any>>(
  accessorKey: string,
  header: string,
  overrides: Partial<MRT_ColumnDef<T>> = {},
  options: CurrencyColumnOptions = {},
): MRT_ColumnDef<T> =>
  ({
    accessorKey,
    header,
    sortingFn: amountSortingFn,
    muiTableBodyCellProps: { align: "right" },
    muiTableHeadCellProps: { align: "right" },
    Cell: ({ cell, row }: any) => (
      <CurrencyCell
        value={cell.getValue()}
        blank={options.blank}
        currency={
          options.currencyField
            ? row?.original?.[options.currencyField]
            : undefined
        }
      />
    ),
    ...overrides,
  }) as MRT_ColumnDef<T>;

/**
 * Applies currency rendering to columns declared as plain data (tableConfig.json,
 * or arrays built from config), by consulting the monetary-field registry.
 *
 * A column may opt in or out explicitly with `format: "currency"` / `format: "none"`;
 * otherwise the registry decides. Columns that already define their own `Cell` are
 * left alone so bespoke renderers are never clobbered.
 */
export const withCurrencyColumns = <T extends Record<string, any>>(
  columns: Array<MRT_ColumnDef<T> & { format?: string }>,
  table?: string,
): Array<MRT_ColumnDef<T>> =>
  columns.map((column) => {
    const key = (column as any).accessorKey as string | undefined;
    if (!key || column.Cell) return column;

    const explicit = column.format;
    const monetary =
      explicit === "currency" ||
      (explicit !== "none" && isMonetaryField(key, table));
    if (!monetary) return column;

    const { format, ...rest } = column;
    return currencyColumn<T>(key, String(column.header ?? key), rest);
  });
